import { useCurrentUser } from '../use-current-user'
import { useWorkspaceSubscription } from './use-workspace-subscription'
import { isUserPremium, canProjectUseFeatureSync } from '@/lib/utils'
import type { Project, WorkspaceSubscriptionPlan } from '@/lib/types'

/**
 * Centralized subscription check hook with migration support
 *
 * Priority order depends on migration status:
 * - Migrated projects: workspace subscription → user premium (skip project subscription)
 * - Unmigrated projects: workspace subscription → project subscription → user premium
 *
 * Migration indicators:
 * - project.workspace_id exists: project is migrated to workspace
 * - project.project_subscription.migrated_to_workspace_id exists: subscription is migrated
 *
 * @param project - Optional project to check project-level subscriptions
 * @returns Feature-level permissions and subscription metadata
 */
export function useSubscriptionCheck(project?: Project) {
  const { data: user } = useCurrentUser()
  const { allSubscriptions, loading } = useWorkspaceSubscription()

  // Migration detection: Check if project or its subscription is migrated
  // Migrated projects should ONLY use workspace subscriptions, not project subscriptions
  const isProjectMigrated = Boolean(project?.workspace_id)
  const isProjectSubscriptionMigrated = Boolean(
    project?.project_subscription?.migrated_to_workspace_id,
  )
  const shouldCheckProjectSubscription =
    project && !isProjectMigrated && !isProjectSubscriptionMigrated
  // Find subscription for the project's workspace
  const wsSubscriptions = allSubscriptions.filter(
    (s) => s.workspace_id === project?.workspace_id,
  )
  if (wsSubscriptions.length > 1) {
    console.error(
      '[useSubscriptionCheck] Multiple subscriptions found for workspace:',
      project?.workspace_id,
      wsSubscriptions,
    )
  }
  const wsSubscription = wsSubscriptions[0]
  // IMPORTANT: Only consider subscriptions with active or past_due status
  // past_due users keep access during grace period while Stripe retries payment
  const isActiveSubscription =
    wsSubscription?.status === 'active' || wsSubscription?.status === 'past_due'
  const wsBasic = isActiveSubscription && wsSubscription?.plan === 'basic'
  const wsPremium = isActiveSubscription && wsSubscription?.plan === 'premium'
  const wsPlatinum = isActiveSubscription && wsSubscription?.plan === 'platinum'
  // Priority order:
  // - For migrated projects: workspace → user (skip project subscription)
  // - For unmigrated projects: workspace → project → user (backward compatibility)
  const hasBasic =
    wsBasic ||
    (shouldCheckProjectSubscription
      ? canProjectUseFeatureSync(project, ['basic', 'premium'])
      : false) ||
    (user ? isUserPremium(user) : false)

  const hasPremium =
    wsPremium ||
    (shouldCheckProjectSubscription
      ? canProjectUseFeatureSync(project, 'premium')
      : false) ||
    (user ? isUserPremium(user) : false)

  const hasPlatinum =
    wsPlatinum ||
    (shouldCheckProjectSubscription
      ? canProjectUseFeatureSync(project, 'platinum')
      : false) ||
    false

  const overwriteRole =
    user?.roles?.includes('super') && user?.roles?.includes('overwrite')

  const minBasic = overwriteRole || hasBasic || hasPlatinum || hasPremium
  const minPremium = overwriteRole || hasPremium || hasPlatinum
  const minPlatinum = overwriteRole || hasPlatinum
  // Feature-level permissions
  const features = {
    connectDomain: minBasic,
    buyDomain: minBasic,
    embeds: minPremium,
    autoSEO: minPremium,
    ecommerce: minPremium,
    payments: minPremium,
    allowProjectVisibilityChange: minBasic,
    canOpenCodeEditor: minBasic,
    allowGaPixelPinterestTiktok: minPremium,
    taskAutomation: minPlatinum,
  }

  /**
   * Determine which subscription is providing access
   * Useful for debugging and display purposes
   */
  const getAccessSource = (): 'workspace' | 'project' | 'user' | 'none' => {
    // For migrated projects, workspace is always the source if it has subscription
    if (isProjectMigrated || isProjectSubscriptionMigrated) {
      if (wsPlatinum || wsPremium || wsBasic) {
        return 'workspace'
      }
      if (user && isUserPremium(user)) {
        return 'user'
      }
      return 'none'
    }

    // For unmigrated projects, check in priority order
    if (wsPlatinum || wsPremium || wsBasic) {
      return 'workspace'
    }
    if (canProjectUseFeatureSync(project, 'premium')) {
      return 'project'
    }
    if (canProjectUseFeatureSync(project, 'basic')) {
      return 'project'
    }
    if (user && isUserPremium(user)) {
      return 'user'
    }
    return 'none'
  }

  // Backward compatibility function
  const checkAccess = (
    requiredPlans: WorkspaceSubscriptionPlan | WorkspaceSubscriptionPlan[],
  ): boolean => {
    if (!user || loading) {
      return false
    }

    const plans = Array.isArray(requiredPlans) ? requiredPlans : [requiredPlans]

    // Check if any required plan is satisfied
    for (const plan of plans) {
      if (plan === 'basic' && hasBasic) {
        return true
      }
      if (plan === 'premium' && hasPremium) {
        return true
      }
      if (plan === 'platinum' && hasPlatinum) {
        return true
      }
    }

    return false
  }

  const hasAccessToBasic = (): boolean => {
    return hasBasic
  }

  const hasAccessToPremium = (): boolean => {
    return hasPremium
  }

  const hasAccessToAnyPaid = (): boolean => {
    return hasBasic
  }

  /**
   * Get the display plan (highest tier available)
   */
  const getDisplayPlan = (): 'free' | 'basic' | 'premium' | 'platinum' => {
    if (hasPlatinum) {
      return 'platinum'
    }
    if (hasPremium) {
      return 'premium'
    }
    if (hasBasic) {
      return 'basic'
    }
    return 'free'
  }
  // console.log('🟢 [SUBSCRIPTION] features:', features)

  return {
    // Feature-level permissions (recommended API)
    features,
    // status,
    status: wsSubscription?.status,

    // Raw access levels
    hasBasic,
    hasPremium,
    hasPlatinum,
    hasAccessToAnyPaid: hasAccessToAnyPaid(),

    // Backward compatibility methods
    hasAccessToBasic,
    hasAccessToPremium,
    checkAccess,

    // Metadata
    plan: getDisplayPlan(),
    accessSource: getAccessSource(),
    loading,
    user,
  }
}
